import locale from "@bonliva-crm/locale";
import { SelectableBulkActionType } from "@bonliva-crm/shared";
import { CircleIcon, Color, DataGrid, Loader } from "@bonliva-crm/ui";
import { Box, makeStyles, Typography } from "@material-ui/core";
import {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridReadyEvent,
  IRowNode,
  RowClassParams,
  RowDoubleClickedEvent,
  RowStyle,
  SelectionChangedEvent
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { BulkActionData } from "../utils/bulk-action-data";

export type ExternalSelectionStepProps<T extends SelectionStepRowType> = {
  rowData: T[];
  isRowSelectable: (params: IRowNode<T>) => boolean;
  colDefs: Array<ColDef<any, any> | ColGroupDef<any>>;
  defaultColumns?: Array<{ label: string; value: string }>;
  isLoading?: boolean;
  onRowClicked?: (event: RowDoubleClickedEvent<T>) => void;
  getRowStyle?: (params: RowClassParams<T>) => RowStyle | undefined;
};

export type InternalSelectionStepProps<T extends SelectionStepRowType> = {
  type: SelectableBulkActionType;
  selectedRows: T[];
  setSelectedRows: (rows: T[]) => void;
  bulkActionData: Record<SelectableBulkActionType, BulkActionData>;
};

export const SelectionStep = <T extends SelectionStepRowType>(
  props: ExternalSelectionStepProps<T> & InternalSelectionStepProps<T>
) => {
  const gridRef = useRef<AgGridReact>(null);
  const classes = useStyles();

  const onRowClicked = useCallback((event: RowDoubleClickedEvent<T>) => {
    const target = event.event?.target;
    if (target && "tagName" in target && target?.tagName === "A") return;

    props.onRowClicked && props.onRowClicked(event);
  }, []);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent<T>) => {
    if (event.source !== "api") {
      props.setSelectedRows(event.api.getSelectedRows());
    }
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent<T>) => {
      const selected: IRowNode[] = [];
      event.api.forEachNode((node) => {
        if (
          props.selectedRows.some((row) => ("id" in row ? row.id : row._id) === node.id)
        ) {
          selected.push(node);
        }
      });
      event.api.setNodesSelected({ nodes: selected, newValue: true });
    },
    [props.selectedRows]
  );

  const getRowId = useCallback(function (params: GetRowIdParams<T>) {
    return "id" in params.data ? params.data.id : params.data._id;
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelectionCurrentPageOnly: true,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        sortable: false,
        suppressMovable: true,
        width: 50,
        resizable: false,
        lockVisible: true
      },
      ...props.colDefs.map((colDef) => ({ ...colDef, sortable: true }))
    ],
    [props.colDefs]
  );

  const selectAll = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.selectAll();
    }
  }, []);

  const deselectAll = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
  }, []);

  if (props.isLoading) {
    return <Loader />;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.textContainer}>
        <Typography component="span" variant="body1">
          {props.bulkActionData[props.type].selectionText}
        </Typography>
        {!props.bulkActionData[props.type].allSelected && (
          <>
            <CircleIcon className={classes.circle} />
            <Typography
              component="span"
              variant="h6"
              className={classes.clickableText}
              onClick={selectAll}
            >
              {locale.selectAll}
            </Typography>
          </>
        )}
        {props.selectedRows.length > 0 && (
          <>
            <CircleIcon className={classes.circle} />
            <Typography
              component="span"
              variant="h6"
              className={classes.clickableText}
              onClick={deselectAll}
            >
              {locale.deselectAll}
            </Typography>
          </>
        )}
      </Box>
      <DataGrid
        ref={gridRef}
        loading={props.isLoading}
        rowData={props.rowData}
        columnDefs={columnDefs}
        rowSelection={"multiple"}
        onRowClicked={onRowClicked}
        suppressRowClickSelection={true}
        rowModelType={"clientSide"}
        maxBlocksInCache={2}
        pagination={true}
        getRowId={getRowId}
        animateRows={true}
        columnNameSelector={{
          localStorageName: "selection-step",
          defaultColumns: props.defaultColumns
        }}
        onSelectionChanged={onSelectionChanged}
        onGridReady={onGridReady}
        isRowSelectable={props.isRowSelectable}
        getRowStyle={props.getRowStyle}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    paddingBottom: "1rem"
  },
  textContainer: {
    display: "flex",
    gap: "6px",
    alignItems: "center",
    padding: theme.spacing(1, 0)
  },
  clickableText: {
    color: Color.PrimaryMain,
    cursor: "pointer"
  },
  circle: {
    width: "4px",
    height: "4px"
  }
}));
